import { useMemo, CSSProperties, useState } from "react";

import * as Popover from "@radix-ui/react-popover";

import AutoLayout from "../AutoLayout";
import Text from "@ui/text";
import DeprecatedIcon from "../DeprecatedIcon";
import { StyleDeclaration, StyleSheet } from "aphrodite";
import { deprecatedColors } from "@src/deprecatedDesignSystem/styles/deprecatedColors";
import { deprecatedTones } from "@src/deprecatedDesignSystem/styles/deprecatedColors";

import TextField from "../TextField";
import HorizontalDivider from "../HorizontalDivider";
import Fuse from "fuse.js";
import Checkbox from "../Checkbox";
import { cn } from "@src/ui/lib/utils";
import { buttonVariants } from "@src/ui/button";

type Option<T> = { value: T; label: string };

type Props<T> = {
  values: T[];
  options: Option<T>[];
  onChange: (values: T[]) => void;
  triggerStyle?: CSSProperties;
  triggerStyleDeclaration?: StyleDeclaration;
  popoverOpenStyleDeclaration?: StyleDeclaration;
  minWidth?: number;
  label: string;
  enableSearch?: boolean;
  getSelectedCopy: (selected: Option<T>[]) => string;
  menuWidth?: number;
};

export default function MultiSelectDropdown<T extends string | number>({
  options,
  values,
  onChange,
  minWidth,
  triggerStyle,
  triggerStyleDeclaration,
  popoverOpenStyleDeclaration,
  enableSearch,
  label,
  getSelectedCopy,
  menuWidth = 300,
}: Props<T>): JSX.Element {
  const [search, setSearch] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const visibleOptions = useMemo(() => {
    if (!search) {
      return options;
    }
    const fuse = new Fuse(options, { threshold: 0.3, keys: ["label"] });
    return fuse.search(search).map((result) => result.item);
  }, [options, search]);
  const selectedValuesSet = useMemo(() => new Set(values), [values]);
  const selectedOptions = useMemo(() => {
    return options.filter((option) => selectedValuesSet.has(option.value));
  }, [options, selectedValuesSet]);
  return (
    <Popover.Root
      onOpenChange={() => {
        setPopoverOpen(!popoverOpen);
      }}
    >
      <Popover.Trigger asChild>
        <AutoLayout
          style={{ minWidth, ...triggerStyle }}
          alignmentVertical="center"
          className={cn(
            buttonVariants({
              variant: "outline",
            }),
            "pr-2 cursor-pointer",
          )}
          styleDeclaration={[
            values.length > 0 && styles.active,
            popoverOpen && popoverOpenStyleDeclaration,
            triggerStyleDeclaration,
          ]}
        >
          <Text
            fontWeight="Medium"
            type="P2"
            color={values.length > 0 ? deprecatedTones.blue10 : undefined}
          >
            {label}:
          </Text>
          {values.length > 0 ? (
            <>
              <Text
                type="P2"
                style={{ marginLeft: 4, marginRight: 2 }}
                color={values.length > 0 ? deprecatedTones.blue10 : undefined}
              >
                {getSelectedCopy(selectedOptions)}
              </Text>
              <DeprecatedIcon
                type="close"
                onClick={() => onChange([])}
                color={deprecatedTones.blue10}
              />
            </>
          ) : (
            <>
              <Text type="P2" style={{ marginLeft: 4, marginRight: 2 }}>
                All
              </Text>
              <DeprecatedIcon type="chevron-down" iconSize={24} />
            </>
          )}
        </AutoLayout>
      </Popover.Trigger>

      <Popover.Content style={{ zIndex: 1000 }} align="end" asChild>
        <AutoLayout
          direction="vertical"
          styleDeclaration={styles.menu}
          width={menuWidth}
        >
          {enableSearch && (
            <>
              <AutoLayout
                paddingVertical={8}
                paddingHorizontal={4}
                alignSelf="stretch"
              >
                <TextField
                  text={search}
                  onTextChange={setSearch}
                  placeholder="Search..."
                />
              </AutoLayout>
              <HorizontalDivider />
            </>
          )}
          <AutoLayout
            direction="vertical"
            paddingVertical={4}
            alignSelf="stretch"
            style={{ overflowY: "auto" }}
          >
            {visibleOptions.map((option) => (
              <AutoLayout
                key={option.value}
                styleDeclaration={styles.option}
                paddingHorizontal={12}
                paddingVertical={4}
                alignmentVertical="center"
                onClick={() => {
                  if (selectedValuesSet.has(option.value)) {
                    onChange(values.filter((value) => value !== option.value));
                  } else {
                    onChange([...values, option.value]);
                  }
                }}
              >
                <Checkbox
                  onChange={() => {}}
                  checked={selectedValuesSet.has(option.value)}
                />
                <Text type="P2" style={{ marginLeft: 8 }}>
                  {option.label}
                </Text>
              </AutoLayout>
            ))}
          </AutoLayout>
        </AutoLayout>
      </Popover.Content>
    </Popover.Root>
  );
}

const styles = StyleSheet.create({
  active: {
    backgroundColor: deprecatedTones.blue2,
    ":hover": {
      backgroundColor: deprecatedTones.blue3,
    },
  },
  menu: {
    backgroundColor: deprecatedColors.background,
    borderRadius: 8,
    boxShadow: "0 8px 28px -12px rgba(0, 0, 0, 0.4)",
    border: `1px solid ${deprecatedTones.gray5Alpha}`,
    marginTop: 4,
    maxHeight: 360,
    zIndex: 100,
  },
  searchContainer: {
    margin: 12,
  },
  option: {
    cursor: "pointer",
    alignSelf: "stretch",
    ":hover": {
      backgroundColor: deprecatedTones.gray5Alpha,
    },
  },
});
